import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getCatalogSellerId, getCatalogStatus } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { getCatalogNetworkStatus, getLiveCatalogStatus } from '@/redux/modules/console';
import { fetchCatalogPresence } from '@/redux/modules/liveCatalogStatus';
import { AuctionStatus } from '@/types/LiveAuctionState';

const useBiddingConsolePageData = () => {
    const dispatch = useAppDispatch();

    const params = useParams<{ catalogId: string }>();
    const catalogId = Number(params?.catalogId);

    const firstRender = useRef(true);

    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));

    const userLoggedIn = useAppSelector((state) => isUserLoggedIn(state));
    const userWasLoggedInPreviously = usePrevious(userLoggedIn);
    const userJustLoggedIn = !userWasLoggedInPreviously && userLoggedIn;

    // Fetch presence and record analytics on first render
    useEffect(() => {
        if (firstRender.current && catalogStatus !== AuctionStatus.Done) {
            dispatch(fetchCatalogPresence(catalogId));
        }
    }, [catalogId, catalogStatus, dispatch]);

    // Fetch catalog registration if user logs in
    useEffect(() => {
        if (userJustLoggedIn && catalogStatus !== AuctionStatus.Done) {
            dispatch(
                fetchLiveCatalogRegistrationByIdIfNeeded({
                    catalogId,
                    sellerId,
                })
            );
        }
    }, [catalogId, catalogStatus, dispatch, sellerId, userJustLoggedIn]);

    useEffect(() => {
        firstRender.current = false;
    }, []);

    return {
        catalogId,
        liveCatalogStatus,
        networkStatus,
    };
};

export default useBiddingConsolePageData;
